import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BrowserTrackerDataSource,
  BrowserTrackerSchemaStore,
  parseBuiltinTrackers,
  type BrowserTrackerSchemaState,
  type TrackerAccessTermination,
  type TrackerIdentity,
} from '@nimbalyst/collab-bundle/trackers-ui';
import type { TrackerBodySeeder } from '@nimbalyst/collab-bundle/editor';
import { TeamSessionError, type TeamJwt, type TeamMemberId } from '../auth/teamJwt';

/**
 * Writes a new item's description into its body room. The seeder carries the
 * Markdown/Lexical codec, so it ships in the editor entry and loads only when
 * someone creates an item with a body.
 */
const seedTrackerBodyLazily: TrackerBodySeeder = async (room, markdown) => {
  const { seedTrackerBody } = await import('@nimbalyst/collab-bundle/editor');
  await seedTrackerBody(room, markdown);
};

/**
 * Which token failures mean this browser is never getting into this room.
 *
 * Mirrors the mapping `BrowserFeedbackRequestClient` already uses for the same
 * `TeamSessionError` kinds, so the two console surfaces cannot disagree about
 * what "signed out" means. Everything not listed -- a network blip, a 5xx from
 * the auth service -- is deliberately absent, so it stays on the engine's retry
 * path instead of being announced as a permission problem.
 */
export function trackerAuthFailure(error: unknown): TrackerAccessTermination | null {
  if (!(error instanceof TeamSessionError)) return null;
  if (error.kind === 'session-expired' || error.kind === 'malformed-token') {
    return { reason: 'signed-out', message: error.message };
  }
  if (
    error.kind === 'wrong-org'
    || error.kind === 'member-mismatch'
    || error.kind === 'organization-unavailable'
  ) {
    return { reason: 'not-a-member', message: error.message };
  }
  return null;
}

export interface BrowserTrackerSessionOptions {
  /** Synchronously disconnect before logout starts purging persisted rows. */
  shutdownSignal?: AbortSignal;
  orgId: string;
  teamProjectId: string;
  teamMemberId: TeamMemberId;
  serverUrl: string;
  /** Null until the org roster resolves; the session waits rather than guessing. */
  identity: TrackerIdentity | null;
  presenceIdentity: { displayName: string; avatarUrl: string | null };
  getTeamJwt: () => Promise<TeamJwt>;
  reportError?: (error: unknown, context: string) => void;
}

export type TeamTypeDeclaration = Parameters<BrowserTrackerSchemaStore['defineTeamType']>[0];

export interface BrowserTrackerSession {
  dataSource: BrowserTrackerDataSource;
  schema: BrowserTrackerSchemaState;
  /**
   * Create a new team type (Pages "New type..."). Resolves once the room has
   * applied it; refused before the room's types have loaded, because only then
   * can an existing id be told apart from a new one. Absent on a read-only
   * session (the public wiki). `syncing` means the room has not answered in
   * time; the definition stays queued for the next connection.
   */
  defineType?: (declared: TeamTypeDeclaration) => Promise<void | { status: 'syncing' }>;
}

/** How long the "New type" dialog waits for the room before it says "syncing", as desktop does. */
export const DEFINE_TYPE_ANSWER_TIMEOUT_MS = 10_000;

/**
 * Define a team type and wait, for a bounded time, for the room to answer.
 *
 * The room's answer is the only thing that settles the store's promise, and a
 * room that never answers (a dropped send, a server that threw) would leave
 * the dialog on "Creating..." for good. A refusal that arrives after the
 * timeout has nobody left to tell: the dialog has already said "syncing".
 */
export async function defineTeamTypeWithTimeout(
  schemaStore: Pick<BrowserTrackerSchemaStore, 'defineTeamType'>,
  dataSource: Pick<BrowserTrackerDataSource, 'status' | 'flushSchemas'>,
  declared: TeamTypeDeclaration,
  timeoutMs = DEFINE_TYPE_ANSWER_TIMEOUT_MS,
): Promise<void | { status: 'syncing' }> {
  if (dataSource.status().status !== 'connected') {
    throw new Error("The team's types are still loading. Try again in a moment.");
  }
  const saved = schemaStore.defineTeamType(declared);
  saved.catch(() => {});
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), timeoutMs); });
  try {
    const answered = Promise.all([saved, dataSource.flushSchemas()]).then(() => 'saved' as const);
    return await Promise.race([answered, timedOut]) === 'timeout' ? { status: 'syncing' } : undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * One tracker room, hosted in this tab.
 *
 * The engine runs in-page against IndexedDB over the **team** JWT. Tracker rooms
 * are team-scoped; the personal JWT is not accepted here and is not reachable
 * from this host at all.
 *
 * The session is keyed by what actually names a room -- org, project, member.
 * Callbacks are held in a ref and never enter the key, because `useNavigate`
 * and friends return a new function on every location change and rebuilding the
 * session would close the socket and replay the room every time someone opened
 * an item.
 *
 * Built in an effect, not a memo: a data source connects the moment it is
 * constructed, and a memo runs twice under StrictMode with no cleanup for the
 * copy it throws away. The effect's cleanup disposes exactly what its setup
 * built. What the hook returns is gated on the current key, so the render that
 * changes scope already returns null rather than the previous room.
 *
 * Construction waits for `identity`. That identity is what every write stamps as
 * `lastModifiedBy` and what the assignment queues match against, and the data
 * source reads it from options that cannot be revised after construction -- so
 * starting without it would mean a session that is quietly wrong about who is
 * using it.
 */
export function useBrowserTrackerSession(
  options: BrowserTrackerSessionOptions,
): BrowserTrackerSession | null {
  const {
    orgId,
    teamProjectId,
    teamMemberId,
    serverUrl,
    identity,
    presenceIdentity,
    getTeamJwt,
    reportError,
    shutdownSignal,
  } = options;

  const callbacks = useRef({ getTeamJwt, reportError, presenceIdentity, identity });
  callbacks.current = { getTeamJwt, reportError, presenceIdentity, identity };

  // `identity !== null` rather than the identity object: a roster refresh that
  // returns an equal-but-new object must not tear the room down.
  const key = identity !== null && !shutdownSignal?.aborted
    ? JSON.stringify([orgId, teamProjectId, teamMemberId, serverUrl])
    : null;

  const [live, setLive] = useState<{
    key: string;
    dataSource: BrowserTrackerDataSource;
    schemaStore: BrowserTrackerSchemaStore;
    schema: BrowserTrackerSchemaState;
  } | null>(null);

  useEffect(() => {
    if (key === null || shutdownSignal?.aborted) return;
    const schemaStore = new BrowserTrackerSchemaStore({
      builtins: parseBuiltinTrackers(),
      reportError: (error, context) => callbacks.current.reportError?.(error, context),
    });
    const dataSource = new BrowserTrackerDataSource({
      workspacePath: `web://${orgId}/${teamProjectId}`,
      serverUrl,
      orgId,
      teamProjectId,
      teamMemberId,
      currentUser: callbacks.current.identity!,
      presenceIdentity: callbacks.current.presenceIdentity,
      getTeamJwt: () => callbacks.current.getTeamJwt(),
      classifyAuthFailure: trackerAuthFailure,
      schemaSync: schemaStore.schemaSync,
      navigationSync: schemaStore.navigationSync,
      reportError: (error, context) => callbacks.current.reportError?.(error, context),
      seedTrackerBody: seedTrackerBodyLazily,
    });
    setLive({ key, dataSource, schemaStore, schema: schemaStore.getState() });
    const unsubscribe = schemaStore.subscribe((schema) => {
      setLive((current) => (current?.dataSource === dataSource ? { ...current, schema } : current));
    });
    let disposed = false;
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      unsubscribe();
      dataSource.dispose();
      schemaStore.dispose();
      setLive((current) => (current?.dataSource === dataSource ? null : current));
    };
    shutdownSignal?.addEventListener('abort', dispose, { once: true });
    return () => {
      shutdownSignal?.removeEventListener('abort', dispose);
      dispose();
    };
    // The key names the scope; the remaining values are its parts.
  }, [key, orgId, teamProjectId, teamMemberId, serverUrl, shutdownSignal]);

  return useMemo(() => {
    if (!live || live.key !== key) return null;
    const { dataSource, schemaStore } = live;
    const defineType = (declared: TeamTypeDeclaration) => defineTeamTypeWithTimeout(schemaStore, dataSource, declared);
    return { dataSource, schema: live.schema, defineType };
  }, [live, key]);
}
